import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Link,
  Breadcrumbs,
  Stack,
  Divider,
} from '@mui/material';
import { OpenInNew, Refresh } from '@mui/icons-material';
import { Deployment } from '@finvari/deliver';
import { useDeploymentStatus } from '../../contexts/DeploymentStatusContext';
import { retryDeployment } from '../../firebase';
import { 
  formatDateTime,
  getDuration,
  getCloudBuildUrl
} from '../../utils/deploymentUtils';

const DeploymentStatus: React.FC = () => {
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const navigate = useNavigate();
  const { getModuleDeploymentsForEnvironment, deployments } = useDeploymentStatus();
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retrySuccess, setRetrySuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!deploymentId) {
      setError('No deployment ID provided');
      setLoading(false);
      return;
    }

    const foundDeployment = deployments?.find(d => d.deploymentId === deploymentId);
    if (foundDeployment) {
      setDeployment(foundDeployment);
      setLoading(false);
    } else if (deployments?.length) {
      // Only show "not found" error once rollouts are loaded
      setError('Deployment not found');
      setLoading(false);
    }
  }, [deploymentId, deployments]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'in_progress':
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Check if this deployment is the most recent for its module/environment combination
  const isLatestDeploymentForModule = (deployment: Deployment): boolean => {
    if (!deployment) return false;
    
    const moduleDeployments = getModuleDeploymentsForEnvironment(deployment.environment);
    const latestForModule = moduleDeployments.get(deployment.module);
    
    return latestForModule?.deploymentId === deployment.deploymentId;
  };

  // Check if retry button should be shown
  const shouldShowRetryButton = (deployment: Deployment): boolean => {
    return deployment.status === 'failed' && isLatestDeploymentForModule(deployment);
  };

  const handleRetry = async () => {
    if (!deployment) return;

    setRetrying(true);
    setRetryError(null);
    setRetrySuccess(null);

    try {
      const result = await retryDeployment(
        deployment.rolloutId,
        deployment.module
      );
      const data = result.data as any;

      if (data?.deployments && data.deployments.length > 0) {
        const newDeployment = data.deployments[0];
        if (newDeployment?.deploymentId) {
          // Navigate to the new deployment
          navigate(`/deployment/${newDeployment.deploymentId}`);
        } else {
          setRetrySuccess(data?.message || `Retry deployment started for ${deployment.module}`);
        }
      } else {
        setRetrySuccess(data?.message || `Retry deployment started for ${deployment.module}`);
      }
    } catch (err: any) {
      setRetryError(err.message || `Failed to retry deployment for ${deployment.module}`);
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading deployment status...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/pull-requests')}
            sx={{ textDecoration: 'none' }}
          >
            Pull Requests
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/deployments')}
            sx={{ textDecoration: 'none' }}
          >
            Deployments
          </Link>
          <Typography variant="body2" color="text.primary">
            Error
          </Typography>
        </Breadcrumbs>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!deployment) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/pull-requests')}
            sx={{ textDecoration: 'none' }}
          >
            Pull Requests
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/deployments')}
            sx={{ textDecoration: 'none' }}
          >
            Deployments
          </Link>
          <Typography variant="body2" color="text.primary">
            Not Found
          </Typography>
        </Breadcrumbs>
        <Alert severity="error">
          Deployment not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/pull-requests')}
            sx={{ textDecoration: 'none' }}
          >
            Pull Requests
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/deployments')}
            sx={{ textDecoration: 'none' }}
          >
            Deployments
          </Link>
          <Typography variant="body2" color="text.primary">
            #{deployment.deploymentId}
          </Typography>
        </Breadcrumbs>

        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Deployment #{deployment.deploymentId}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={deployment.status.replace('_', ' ')}
              color={getStatusColor(deployment.status)}
              size="medium"
            />
            {deployment.buildId && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNew />}
                component={Link}
                href={getCloudBuildUrl(deployment.buildId, deployment.environment)}
                target="_blank"
              >
                View in Cloud Build Console
              </Button>
            )}
            {shouldShowRetryButton(deployment) && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<Refresh />}
                onClick={handleRetry}
                disabled={retrying}
              >
                {retrying ? 'Retrying...' : 'Retry Deployment'}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Retry Success/Error Messages */}
      {retrySuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {retrySuccess}
          </Typography>
        </Alert>
      )}
      
      {retryError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {retryError}
          </Typography>
        </Alert>
      )}

      {/* Status Details */}
      {deployment.lastStatusDetails && (
        <Alert 
          severity={getStatusColor(deployment.status) === 'error' ? 'error' : 'info'}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            {deployment.lastStatusDetails}
          </Typography>
        </Alert>
      )}

      {/* Deployment Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Deployment Details
          </Typography>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600, width: '200px' }}>
                    Project
                  </TableCell>
                  <TableCell>{deployment.projectName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    Environment
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deployment.environment}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    Branch
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {deployment.branch}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    Module
                  </TableCell>
                  <TableCell>{deployment.module}</TableCell>
                </TableRow>
                {deployment.prNumber && (
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Pull Request
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          #{deployment.prNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {deployment.prTitle}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {deployment.prAuthor && (
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Author
                    </TableCell>
                    <TableCell>{deployment.prAuthor}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    Created By
                  </TableCell>
                  <TableCell>{deployment.createdBy}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    Created
                  </TableCell>
                  <TableCell>{formatDateTime(deployment.createdAt)}</TableCell>
                </TableRow>
                {deployment.finishedAt && (
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Completed
                    </TableCell>
                    <TableCell>{formatDateTime(deployment.finishedAt)}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    Duration
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {getDuration(deployment.createdAt, deployment.finishedAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
                {deployment.lastUpdated && (
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Last Updated
                    </TableCell>
                    <TableCell>{formatDateTime(deployment.lastUpdated)}</TableCell>
                  </TableRow>
                )}
                {deployment.commitHash && (
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Commit Hash
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {deployment.commitHash}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {deployment.commitMessage && (
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Commit Message
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {deployment.commitMessage}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Error Details */}
      {deployment.error && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="error">
              Error Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Alert severity="error">
              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                {deployment.error}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* In Progress Indicator */}
      {deployment.status === 'in_progress' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Deployment in progress...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This page will update automatically when the deployment completes.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default DeploymentStatus;